import Konva from "konva";
import BoundingBox from "frontend/geometry/bounding-box";
import { clamp, viewportToStage } from "utils/math-utils";
import consts from "shared/consts";
import { PosScale } from "frontend/konva-stage-types";
import { Point } from "shared/datamodel/schemas";
import { getBoundingBox } from "frontend/utils/node-utils";

const STAGE_PADDING_PERCENT = 0.15;
const MAX_WAIT_TIME = 1000;
const CHECK_INTERVAL = 10;
const DEFAULT_ANIMATION_DURATION = 300;

/**
 * Helper function to find nodes by their element IDs
 */
export function findNodesByIds(layer: Konva.Layer | null | undefined, elementIds: string[]): Konva.Node[] {
  if (!layer) {
    return [];
  }

  return layer
    .find((node: unknown) => {
      const konvaNode = node as Konva.Node;
      return elementIds.includes(konvaNode.attrs.id);
    })
    .toArray();
}

/**
 * Waits for elements to render and returns the found nodes
 * @returns Promise that resolves with the found nodes, or empty array if not found
 */
export async function waitForElements(
  layer: Konva.Layer | null | undefined,
  stage: Konva.Stage | null | undefined,
  elementIds: string[]
): Promise<Konva.Node[]> {
  if (!layer || !stage) {
    return [];
  }

  return new Promise<Konva.Node[]>((resolve) => {
    const interval = setInterval(() => {
      if (!layer || !stage) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve([]);
        return;
      }

      const nodes = findNodesByIds(layer, elementIds);

      if (nodes.length > 0) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve(nodes);
      }
    }, CHECK_INTERVAL);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      resolve([]);
    }, MAX_WAIT_TIME);
  });
}

function getStageBbox(stage: Konva.Stage) {
  const layer: Konva.Layer | undefined = stage.getLayers()[0] as Konva.Layer;
  // Known issue with konva
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rect = layer?.getClientRect({ relativeTo: stage as any });
  if (!rect) return null;
  return BoundingBox.fromRect(rect);
}

function getElementBbox(stage: Konva.Stage, element: Konva.Node) {
  // Known issue with konva
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rect = element.getClientRect({ relativeTo: stage as any });
  return BoundingBox.fromRect(rect);
}

function getTargetPosScale(stage: Konva.Stage, box: BoundingBox, padding: number) {
  if (box.width > 0 && box.height > 0) {
    const maxDimension = Math.max(box.width, box.height);
    const absolutePadding = maxDimension * padding;
    box = box.addPadding(absolutePadding);
    const scale = clamp(
      Math.min(stage.width() / box.width, stage.height() / box.height),
      consts.STAGE_ZOOM.MIN,
      1 // Max 100% zoom
    );
    const x = stage.width() / 2 - (box.x + box.width / 2) * scale;
    const y = stage.height() / 2 - (box.y + box.height / 2) * scale;
    return { x, y, scale };
  } else {
    const x = stage.width() / 2 - box.x;
    const y = stage.height() / 2 - box.y;
    return { x, y, scale: 1 };
  }
}

export function fitToElement(
  stageRef?: React.RefObject<Konva.Stage>,
  element?: Konva.Node,
  padding = STAGE_PADDING_PERCENT
) {
  if (!stageRef?.current || !element) return null;
  const bbox = getElementBbox(stageRef.current, element);
  const viewBox = getTargetPosScale(stageRef.current, bbox, padding);
  return viewBox;
}

function getNewCenterPositionForBoundingBox(
  stage: Konva.Stage,
  box: BoundingBox,
  currentPosScale: PosScale,
  padding: number
): Point | null {
  const currentScale = currentPosScale.scale;
  const maxDimension = Math.max(box.width, box.height);
  const absolutePadding = maxDimension * padding;
  const paddedBox = box.addPadding(absolutePadding);

  // Check if the box can fit on stage at current scale
  const scaledWidth = paddedBox.width * currentScale;
  const scaledHeight = paddedBox.height * currentScale;

  if (scaledWidth > stage.width() || scaledHeight > stage.height()) {
    return null;
  }

  const currentViewport = viewportToStage(currentPosScale, {
    x: 0,
    y: 0,
    width: stage.width(),
    height: stage.height(),
  });

  let newX = currentPosScale.x;
  let newY = currentPosScale.y;

  // Check if we need to move horizontally
  if (paddedBox.x < currentViewport.x) {
    // New elements are to the left of viewport
    newX = -paddedBox.x * currentScale;
  } else if (paddedBox.x + paddedBox.width > currentViewport.x + currentViewport.width) {
    // New elements are to the right of viewport
    newX = stage.width() - (paddedBox.x + paddedBox.width) * currentScale;
  }

  // Check if we need to move vertically
  if (paddedBox.y < currentViewport.y) {
    // New elements are above viewport
    newY = -paddedBox.y * currentScale;
  } else if (paddedBox.y + paddedBox.height > currentViewport.y + currentViewport.height) {
    // New elements are below viewport
    newY = stage.height() - (paddedBox.y + paddedBox.height) * currentScale;
  }

  return { x: newX, y: newY };
}

export async function waitForFitToNewElements(
  newIds: Set<string>,
  layerRef: Konva.Layer,
  stageRef: Konva.Stage,
  posScale: PosScale
): Promise<PosScale | null> {
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      if (layerRef.children.toArray().some((child) => newIds.has(child.attrs.id))) {
        clearInterval(interval);
        clearTimeout(timeout);

        // Get the current viewport (what's visible on screen)
        const currentViewport = BoundingBox.fromRect(
          viewportToStage(posScale, { x: 0, y: 0, width: window.innerWidth, height: window.innerHeight })
        );

        const newElements = layerRef.children.toArray().filter((child) => newIds.has(child.attrs.id));

        // Get the bounding box of only the new elements
        const newElementsBbox = BoundingBox.expandOnAll(
          newElements.map((node) => {
            return node.getClientRect({
              relativeTo: node.getStage() as any,
              skipShadow: true,
            });
          })
        );

        if (
          !BoundingBox.isValid(newElementsBbox) ||
          currentViewport.contains(newElementsBbox) ||
          newElementsBbox.width <= 0 ||
          newElementsBbox.height <= 0
        ) {
          resolve(null);
          return;
        }

        // First try to fit by just moving the viewport at current zoom level

        const newCenterPosition = getNewCenterPositionForBoundingBox(
          stageRef,
          newElementsBbox,
          posScale,
          STAGE_PADDING_PERCENT
        );

        if (newCenterPosition) {
          resolve({ x: newCenterPosition.x, y: newCenterPosition.y, scale: posScale.scale });
        } else {
          // Need to zoom out to fit
          const targetViewport = getTargetPosScale(stageRef, newElementsBbox, STAGE_PADDING_PERCENT);
          resolve(targetViewport);
        }
      }
    }, CHECK_INTERVAL);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      reject(null);
    }, MAX_WAIT_TIME);
  });
}

export function animateToFitStage(
  stageRef: Konva.Stage,
  targetPosScale: PosScale,
  onUpdate: (posScale: PosScale) => void,
  duration = DEFAULT_ANIMATION_DURATION
) {
  // Stop any existing animation
  stageRef.stopDrag();

  // Create tween for smooth animation
  const tween = new Konva.Tween({
    node: stageRef,
    duration: duration / 1000, // Konva uses seconds
    easing: Konva.Easings.EaseInOut,
    x: targetPosScale.x,
    y: targetPosScale.y,
    scaleX: targetPosScale.scale,
    scaleY: targetPosScale.scale,
    onUpdate: function () {
      // Update the position scale state during animation
      const currentPosScale = {
        x: stageRef.x(),
        y: stageRef.y(),
        scale: stageRef.scaleX(),
      };
      onUpdate(currentPosScale);
    },
    onFinish: function () {
      // Ensure final state is exactly the target
      onUpdate(targetPosScale);
    },
  });

  tween.play();

  return tween;
}

export function fitToStage(stageRef?: React.RefObject<Konva.Stage>, padding = STAGE_PADDING_PERCENT) {
  if (!stageRef?.current) return null;
  const bbox = getStageBbox(stageRef.current);
  if (!bbox) return null;
  const viewBox = getTargetPosScale(stageRef.current, bbox, padding);
  return viewBox;
}

/**
 * Centers the canvas viewport to show an array of elements.
 * This function calculates the bounding box of all provided elements and adjusts
 * the viewport to center them with appropriate padding.
 *
 * @param stageRef - Reference to the Konva stage
 * @param elements - Array of Konva nodes to center in the viewport
 * @param padding - Padding percentage around the elements (default: 0.15)
 * @param options - Additional options for centering behavior
 * @returns The new PosScale (position and scale) for the viewport, or null if invalid
 */
export function centerToElements(
  stageRef: React.RefObject<Konva.Stage>,
  elements: Konva.Node[],
  padding = STAGE_PADDING_PERCENT,
  options?: {
    maintainCurrentScale?: boolean; // If true, tries to maintain current zoom level
    animate?: boolean; // If true, returns animation parameters
    animationDuration?: number; // Duration of animation in milliseconds
  }
): PosScale | null {
  if (!stageRef.current || !elements || elements.length === 0) {
    return null;
  }

  const stage = stageRef.current;

  // Calculate bounding box for all elements
  const elementRects = elements.map((element) => {
    return element.getClientRect({
      relativeTo: stage as any,
      skipShadow: true,
    });
  });

  const combinedBbox = BoundingBox.expandOnAll(elementRects);

  // Check if the bounding box is valid
  if (!BoundingBox.isValid(combinedBbox) || combinedBbox.width <= 0 || combinedBbox.height <= 0) {
    return null;
  }

  // If maintainCurrentScale is true, try to center without changing zoom
  if (options?.maintainCurrentScale) {
    const currentScale = stage.scaleX();
    const currentPosScale: PosScale = {
      x: stage.x(),
      y: stage.y(),
      scale: currentScale,
    };

    const newPosition = getNewCenterPositionForBoundingBox(stage, combinedBbox, currentPosScale, padding);

    if (newPosition) {
      return {
        x: newPosition.x,
        y: newPosition.y,
        scale: currentScale,
      };
    }
  }

  // Calculate the position and scale to fit all elements
  const targetPosScale = getTargetPosScale(stage, combinedBbox, padding);

  return targetPosScale;
}

/**
 * Centers the canvas viewport to show an array of elements with animation.
 * This is a wrapper around centerToElements that also handles the animation.
 *
 * @param stageRef - Reference to the Konva stage
 * @param elements - Array of Konva nodes to center in the viewport
 * @param onUpdate - Callback function called during animation with current PosScale
 * @param padding - Padding percentage around the elements (default: 0.15)
 * @param options - Additional options for centering and animation
 * @returns The Konva.Tween object for the animation, or null if centering failed
 */
export function animateCenterToElements(
  stageRef: React.RefObject<Konva.Stage>,
  elements: Konva.Node[],
  onUpdate: (posScale: PosScale) => void,
  padding = STAGE_PADDING_PERCENT,
  options?: {
    maintainCurrentScale?: boolean;
    animationDuration?: number;
  }
): Konva.Tween | null {
  const targetPosScale = centerToElements(stageRef, elements, padding, {
    maintainCurrentScale: options?.maintainCurrentScale,
  });

  if (!targetPosScale || !stageRef.current) {
    return null;
  }

  return animateToFitStage(
    stageRef.current,
    targetPosScale,
    onUpdate,
    options?.animationDuration || DEFAULT_ANIMATION_DURATION
  );
}
export function fitToSelectedElements(
  stageRef?: React.RefObject<Konva.Stage>,
  selectedElementIds?: string[],
  padding = STAGE_PADDING_PERCENT
) {
  if (!stageRef?.current || !selectedElementIds || selectedElementIds.length === 0) return null;

  const stage = stageRef.current;
  const layer = stage.getLayers()[0] as Konva.Layer;

  // Get all selected elements
  const selectedElements = selectedElementIds
    .map((id) => layer.findOne(`#${id}`))
    .filter((node): node is Konva.Node => !!node);

  if (selectedElements.length === 0) return null;

  const selectedElementsBoundingBox = BoundingBox.expandOnAll(
    selectedElements.map((node) => {
      // For elements with proper element data, use getBoundingBox which handles tables correctly
      if (
        node.attrs?.element &&
        (node.attrs.element.type === consts.CANVAS_ELEMENTS.TABLE ||
          node.attrs.element.type === consts.CANVAS_ELEMENTS.FRAME)
      ) {
        const boundingBox = getBoundingBox(node.attrs.element.type, node.attrs.element);

        // If getBoundingBox returns zero dimensions, fall back to getClientRect
        if (boundingBox && boundingBox.width > 0 && boundingBox.height > 0) {
          return boundingBox;
        }
      }

      return node.getClientRect({
        relativeTo: stage as unknown as Konva.Container,
        skipShadow: true,
      });
    })
  );

  if (
    !BoundingBox.isValid(selectedElementsBoundingBox) ||
    selectedElementsBoundingBox.width <= 0 ||
    selectedElementsBoundingBox.height <= 0
  ) {
    return null;
  }

  const viewBox = getTargetPosScale(stage, selectedElementsBoundingBox, padding);
  return viewBox;
}
